import { IonContent, IonPage, useIonRouter } from "@ionic/react";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useLocation, useParams } from "react-router-dom";

import { ActionBar } from "../components/ActionBar";
import { Button } from "../components/Button";
import { Chip } from "../components/Chip";
import { Field } from "../components/Fields";
import { Hero } from "../components/Hero";
import { I } from "../components/icons";
import { Notice } from "../components/Notice";
import {
  QuestionFollowUp,
  QuestionHead,
  QuestionOptions,
  QuestionProgress,
  type QuestionOption,
} from "../components/Question";
import { NavRow, RowGroup } from "../components/Rows";
import { Segmented } from "../components/Segmented";
import { Sheet } from "../components/Sheet";
import { Screen } from "../components/Shell";
import { Tag } from "../components/Tag";
import { TopBar } from "../components/TopBar";
import { age } from "../domain/body";
import { dayLabel, todayStr } from "../domain/date";
import { older } from "../domain/eating";
import {
  CONDITION_OPTS,
  HYPO_OPTS,
  MED_OPTS,
  PREGNANCY_AGES,
  PREGNANT_OPTS,
  SLEEP_OPTS,
  pruneAnswers,
} from "../domain/eatingQuestions";
import { curWeight } from "../domain/energy";
import { aboutWhom, type Pronouns } from "../domain/family";
import { shortTestsTitle } from "../domain/fitnessTests";
import {
  ACTIVE_OPTS,
  BEFORE_ROWS,
  BP_CONTROL_OPTS,
  BREATHLESS_OPTS,
  CHAIR_OPTS,
  DAYS_OPTS,
  FALLS_OPTS,
  FEET_OPTS,
  FLOOR_OPTS,
  GOAL_OPTS,
  HAVE_OPTS,
  JOINT_HOW_OPTS,
  JOINT_OPTS,
  MEAL_WALK_OPTS,
  MINUTES_OPTS,
  RECENT_OPTS,
  SIDED,
  SIDE_OPTS,
  STYLE_OPTS,
  UNSTEADY_OPTS,
  WHEN_OPTS,
  chairAnswer,
  chairAsked,
  chairPatch,
  daysLine,
  goalsOffered,
  haveLine,
  jointLine,
  latestTests,
  mealWalkLine,
  placement,
  planLine,
  readBack,
  stepAnswered,
  stillRight,
  testsOffered,
  trainingCount,
  trainingPatch,
  trainingScreens,
  trainingSteps,
  whatChanges,
  type ScreenId,
  type StillKey,
  type TestKey,
  type TrainingDraftAnswers,
} from "../domain/trainingQuestions";
import { weightSeries } from "../domain/trend";
import type {
  DayTests,
  EatingAnswers,
  EatingCondition,
  EatingProfile,
  JointKey,
  KayaState,
  TrainingAnswers,
  TrainingProfile,
} from "../domain/types";
import type { SafetyBefore } from "../storage/local";
import { deviceAnswers, useKaya } from "../store/kayaStore";
import { setEatingProfile, setTrainingProfile } from "../store/toggles";
import "./TrainQuestions.css";

type Screen_ = ScreenId | "start" | "readback";

const NONE = "none";
const YES_NO = [
  { value: "yes" as const, label: "Yes" },
  { value: "no" as const, label: "No" },
];

/** The block without its merge stamps: what the screens edit. */
function draftOf(t: TrainingProfile | undefined): TrainingDraftAnswers {
  if (!t) return {};
  const { at: _at, haveAt: _a, haveOff: _b, jointsAt: _c, jointsOff: _d, ...rest } = t;
  return rest;
}

function eatingOf(e: EatingProfile | undefined): EatingAnswers {
  if (!e) return {};
  const { at: _at, avoidAt: _a, avoidOff: _b, dislikesAt: _c, dislikesOff: _d, ...rest } = e;
  return rest;
}

function toggle<T extends string>(list: readonly T[] | undefined, v: T): T[] {
  const cur = list ?? [];
  return cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v];
}

/** Food's answers with the ones given on this flow's own conditions screen laid over them. */
function withHealth(S: KayaState, health: EatingAnswers | undefined): KayaState {
  return health ? { ...S, profile: { ...S.profile, eating: { ...S.profile.eating, ...health } } } : S;
}

function anyYes(b: SafetyBefore | undefined): boolean {
  return !!b && (!!b.chest || !!b.faint || !!b.breath || !!b.supervised);
}

/**
 * Someone else's answers, filled in through a linked account (ADR-046, ADR-053). The questions
 * are the same except the two safety screens, which are the person's own and never leave their
 * phone; the tests step offers typed counts instead of the timer. P6 wires this from the family
 * page; `save` writes the training block (and food's shared answers when this flow asked them,
 * and any typed test counts) through the link.
 */
export interface TrainingSubject {
  S: KayaState;
  /** what the page calls them ("Mum") */
  name: string;
  pron: Pronouns;
  /** the writer's own name, saved as `setBy` ("set by Udbhav") */
  by: string;
  /** where Back and a finished save go */
  back: string;
  save: (answers: TrainingAnswers, extra: { eating?: EatingAnswers; tests?: Omit<DayTests, "at"> }) => Promise<void>;
}

/**
 * "Plan my exercise" (plan 2026-10-05 §3.5, §3.6; boards TrainingStart … TrainingReadBack;
 * ADR-053). The food questionnaire's machinery: one question a screen at the large scale, each
 * option saying what it changes, a draft on this phone until the read-back saves, and the answers
 * food already holds read back on one "Still right?" step rather than asked again.
 */
export default function TrainQuestions() {
  return (
    <IonPage>
      <TrainingQuestions />
    </IonPage>
  );
}

export function TrainingQuestions({ subject }: { subject?: TrainingSubject }) {
  const own = useKaya((s) => s.S);
  const S = subject?.S ?? own;
  const meta = useKaya((s) => s.meta);
  const update = useKaya((s) => s.update);
  const showToast = useKaya((s) => s.showToast);
  const setTrainingDraft = useKaya((s) => s.setTrainingDraft);
  const setTrainingSafety = useKaya((s) => s.setTrainingSafety);
  const setPlayerPrefs = useKaya((s) => s.setPlayerPrefs);
  const router = useIonRouter();
  const location = useLocation();
  const params = useParams<{ screen?: string }>();
  const from = subject?.back ?? (new URLSearchParams(location.search).get("from") === "you" ? "/you/training" : "/train");
  const today = new Date();

  // A draft is resumed only while nothing has been saved since it began, on this phone or another.
  const init = useMemo(() => {
    const d = subject ? undefined : meta.trainingDraft;
    const savedAt = S.profile.training?.at;
    const safety = subject ? undefined : meta.trainingSafety;
    if (d && d.since === savedAt) return d;
    return {
      answers: draftOf(S.profile.training),
      health: S.profile.eating?.at === undefined ? eatingOf(S.profile.eating) : undefined,
      before: safety?.before,
      recent: safety ? (safety.recent === "none" ? [] : safety.recent.split(",").filter(Boolean)) : undefined,
      step: "start",
      since: savedAt,
    };
    // the draft is read once, when the questionnaire opens
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [a, setA] = useState<TrainingDraftAnswers>(init.answers);
  const [health, setHealth] = useState<EatingAnswers | undefined>(init.health);
  const [before, setBefore] = useState<SafetyBefore | undefined>(init.before);
  const [recent, setRecent] = useState<string[] | undefined>(init.recent);
  const [screen, setScreen] = useState<Screen_>((params.screen as Screen_ | undefined) ?? (init.step as Screen_));
  const [change, setChange] = useState<StillKey | null>(null);
  const [typed, setTyped] = useState<{ chair: string; tandem: string }>({ chair: "", tandem: "" });
  const content = useRef<HTMLIonContentElement>(null);

  useEffect(() => {
    if (!subject) setTrainingDraft({ answers: a, health, before, recent, step: screen, since: init.since });
  }, [a, health, before, recent, screen, init.since, setTrainingDraft, subject]);

  const set = (patch: Partial<TrainingDraftAnswers>) => setA((cur) => ({ ...cur, ...patch }));
  const setH = (patch: Partial<EatingAnswers>) => setHealth((cur) => ({ ...(cur ?? {}), ...patch }));

  // the person's state as the screens should read it: this flow's own health answers laid over
  const Sx = withHealth(S, health);
  const gate = !subject && (anyYes(before) || !!recent?.length);
  const asking = { subject: !!subject, gate };
  const screens = trainingScreens(Sx, a, today, asking);
  const cur: Screen_ =
    screen === "start" || screen === "readback" || screens.includes(screen) ? screen : screens[0];
  const idx = cur === "start" || cur === "readback" ? -1 : screens.indexOf(cur);
  const device = deviceAnswers(undefined, gate ? "doctor" : "ok");
  const isOlder = older(S, today);
  const tested = subject ? typedTests(typed, today) : latestTests(S);

  const go = (s: Screen_) => {
    setScreen(s);
    void content.current?.scrollToTop(0);
  };
  const next = () => {
    if (cur === "joints" && a.joints === undefined) set({ joints: {} });
    // re-opened at one answer (You › Training, "My doctor said yes"): straight to the read-back
    if (idx === screens.length - 1 || (params.screen && cur === params.screen && S.profile.training?.at !== undefined))
      return go("readback");
    go(screens[idx + 1]);
  };
  const back = () => go(idx <= 0 ? "start" : screens[idx - 1]);
  const lastScreen = screens[screens.length - 1];

  const save = () => {
    const patch = trainingPatch(Sx, a, today, { asking, by: subject?.by });
    const eating = health ? pruneAnswers(Sx, { ...eatingOf(S.profile.eating), ...health }, today) : undefined;
    if (subject) {
      const tests = typedTests(typed, today);
      subject
        .save(patch, { eating, tests: tests ? withoutStamp(tests.tests) : undefined })
        .then(
          () => router.push(from, "back"),
          (e: unknown) => showToast(e instanceof Error ? e.message : "Could not save — try again")
        );
      return;
    }
    update((d) => {
      if (eating) setEatingProfile(d, eating);
      setTrainingProfile(d, patch);
      // the treadmill chip is the treadmill question (ADR-022): one answer, not two
      if (patch.have) d.profile.hasTreadmill = patch.have.includes("treadmill");
    });
    setTrainingSafety({
      gate: gate ? "doctor" : "ok",
      recent: recent?.length ? recent.join(",") : "none",
      before,
      at: Date.now(),
    });
    setPlayerPrefs({ voice: a.voice ?? false, lang: a.lang ?? "en" });
    setTrainingDraft(undefined);
    showToast("Answers saved");
    router.push(from === "/you/training" ? from : "/train", "back");
  };

  const answered = (s: Screen_): boolean => {
    switch (s) {
      case "health":
        return (
          (!pregnancyAsked(Sx, today) || !!health?.pregnant) &&
          stepAnswered(Sx, a, "health", today) &&
          (!(health?.conditions ?? []).includes("diabetes") || !!health?.hypoAnswer) &&
          (!medsAsked(Sx, today) || health?.meds !== undefined)
        );
      case "before":
        return BEFORE_ROWS.every((r) => before?.[r.key] !== undefined);
      case "recent":
        return recent !== undefined;
      case "joints":
        return a.joints !== undefined && stepAnswered(Sx, a, "jointHow", today);
      case "start":
      case "readback":
      case "tests":
        return true;
      default:
        return stepAnswered(Sx, a, s, today);
    }
  };

  const reasonFor = (s: Screen_): string => {
    if (s === "goals") return "Pick at least one";
    if (s === "before") return "Answer all four";
    if ((s === "still" || s === "health" || s === "when" || s === "joints") && followUpOpen(s)) return "Answer the follow-up too";
    if (s === "body" && !chairAsked(Sx, a, today)) return "Pick an answer";
    if (s === "days" || s === "falls" || s === "body") return "Answer both questions";
    return "Pick an answer";
  };
  const followUpOpen = (s: Screen_): boolean => {
    const conds = Sx.profile.eating?.conditions ?? [];
    if (s === "still" || s === "health") return conds.includes("bp") || conds.includes("diabetes");
    if (s === "when") return a.when === "meals";
    if (s === "joints") return Object.keys(a.joints ?? {}).length > 0;
    return false;
  };

  const countWords = (id: Parameters<typeof trainingCount>[2]) => {
    const c = trainingCount(Sx, a, id, today, asking);
    return c ? `Question ${c.n} of ${c.of}` : undefined;
  };

  const head = (
    <TopBar
      title={subject ? `${subject.name}’s exercise answers` : "Plan my exercise"}
      back={
        subject
          ? { to: from, label: `Back to ${subject.name}` }
          : cur === "start"
            ? { to: from, label: from === "/train" ? "Back to Train" : "Back to Training" }
            : { to: from, label: `Close, back to ${from === "/train" ? "Train" : "Training"}. Answers so far are kept`, close: true }
      }
    />
  );

  /* ---------- the BP and diabetes follow-ups, shared by Still right? and the conditions screen ---------- */

  const bpFollow = (
    <QuestionFollowUp
      key="bp"
      question="Is your BP usually under control?"
      line="No or Not sure: no holds held hard and no fast work, and a line to ask your doctor."
    >
      <Segmented
        ariaLabel="BP usually under control"
        options={BP_CONTROL_OPTS}
        value={a.bpControlled ?? null}
        onChange={(v) => set({ bpControlled: v })}
      />
      <BpReading value={a.bpReading} onChange={(v) => set({ bpReading: v })} />
    </QuestionFollowUp>
  );
  const sugarFollow = (
    <QuestionFollowUp
      key="sugar"
      question="Numb feet, or a sore on a foot that is slow to heal?"
      line={
        a.feet === "sore"
          ? "Chair and bed moves only until it heals."
          : a.feet === "numb"
            ? "Shoes on for every standing move, and check your feet after."
            : undefined
      }
    >
      <Segmented ariaLabel="Your feet" options={FEET_OPTS} value={a.feet ?? null} onChange={(v) => set({ feet: v })} />
      <p className="k-qfollow__q">Laser or injections in your eyes, for diabetes?</p>
      <Segmented
        ariaLabel="Eye treatment for diabetes"
        options={YES_NO}
        value={a.eyesTreated === undefined ? null : a.eyesTreated ? "yes" : "no"}
        onChange={(v) => set({ eyesTreated: v === "yes" })}
      />
      {a.eyesTreated ? <p className="k-qfollow__line">No heads-down holds and no hard effort.</p> : null}
    </QuestionFollowUp>
  );

  /* ---------- the screens ---------- */

  const body = (): ReactNode => {
    switch (cur) {
      case "start":
        return renderStart();

      case "readback":
        return renderReadBack();

      case "still": {
        const about = aboutWhom(subject ? subject.pron : null);
        const lines = stillRight(Sx, today).map((l) => ({ ...l, text: about(l.text) }));
        const conds = Sx.profile.eating?.conditions ?? [];
        return (
          <div className="k-q__block">
            <QuestionHead
              id="q-still"
              title="Still right?"
              help={subject ? `From ${subject.pron.their} Plan my food answers.` : "From your Plan my food answers. Change any that are not."}
            />
            <div className="k-rows" role="list">
              {lines.map((l) => (
                <div key={l.key} role="listitem" className="k-qres">
                  <span className="k-row__txt">
                    <span className="k-row__title">{l.text}</span>
                  </span>
                  {subject ? null : (
                    <Button
                      variant="text"
                      ariaLabel={`Change: ${l.text}`}
                      onClick={() => (l.key === "periods" ? router.push("/you/body", "forward") : setChange(l.key))}
                    >
                      Change
                    </Button>
                  )}
                </div>
              ))}
            </div>
            {conds.includes("bp") ? <div className="k-qfree">{bpFollow}</div> : null}
            {conds.includes("diabetes") ? <div className="k-qfree">{sugarFollow}</div> : null}
            <p className="k-qfine">
              {subject
                ? `Change these under ${subject.name}’s Eating answers.`
                : "A change here changes Plan my food too."}
            </p>
          </div>
        );
      }

      case "health":
        return renderHealth();

      case "before":
        return (
          <div className="k-q__block">
            <QuestionHead id="q-before" title="Before you start" help="In the last year, have you had any of these?" />
            <div className="k-qyn">
              {BEFORE_ROWS.map((r) => {
                const v = before?.[r.key];
                return (
                  <div key={r.key} className={`k-qyn__row${v ? " is-yes" : ""}`}>
                    <p className="k-qyn__q">{r.label}</p>
                    <Segmented
                      ariaLabel={r.label}
                      options={YES_NO}
                      value={v === undefined ? null : v ? "yes" : "no"}
                      onChange={(x) => setBefore((b) => ({ ...(b ?? {}), [r.key]: x === "yes" }))}
                    />
                  </div>
                );
              })}
            </div>
            {anyYes(before) ? (
              <Notice tone="caution">
                Talk to your doctor first. Until then Kaya suggests easy walks and the gentle sessions. Tell Kaya when they say yes.
              </Notice>
            ) : null}
            <p className="k-qfine">A yes keeps the plan at easy walks until a doctor says otherwise. These answers stay on this phone.</p>
          </div>
        );

      case "recent": {
        const opts: QuestionOption<string>[] = [...RECENT_OPTS, { value: NONE, label: "None" }];
        const chosen = recent === undefined ? [] : recent.length ? recent : [NONE];
        return (
          <div className="k-q__block">
            <QuestionHead id="q-recent" title="Anything in the last six months?" help="Pick all that apply." />
            <QuestionOptions
              labelledBy="q-recent"
              options={opts}
              chosen={chosen}
              onPick={(v) => setRecent(v === NONE ? [] : toggle(recent, v))}
            />
            {recent?.length ? (
              <Notice tone="caution">
                Talk to your doctor first. Until then Kaya suggests easy walks and the gentle sessions. Tell Kaya when they say yes.
              </Notice>
            ) : null}
            <p className="k-qfine">These answers stay on this phone.</p>
          </div>
        );
      }

      case "goals": {
        const offered = goalsOffered(Sx);
        const g = a.goals ?? [];
        const opts: QuestionOption<string>[] = GOAL_OPTS.filter((o) => offered.includes(o.value)).map((o) => {
          const i = g.indexOf(o.value);
          return {
            value: o.value,
            label: o.label,
            sub: i < 0 ? o.sub : `${i === 0 ? "1st" : "2nd"} · ${o.sub}`,
            disabled: i < 0 && g.length >= 2,
          };
        });
        return (
          <div className="k-q__block">
            <QuestionHead id="q-goals" title="What do you want most?" help="Pick up to two, the most important first." />
            <QuestionOptions
              labelledBy="q-goals"
              options={opts}
              chosen={g}
              onPick={(v) => set({ goals: toggle(g, v as (typeof g)[number]) })}
            />
            {offered.includes("sugar") || offered.includes("bp") ? (
              <p className="k-qfine">
                {offered.includes("sugar") && offered.includes("bp")
                  ? "Blood sugar and BP are here because of your health answers."
                  : offered.includes("sugar")
                    ? "Blood sugar is here because of your health answers."
                    : "BP is here because of your health answers."}
              </p>
            ) : null}
          </div>
        );
      }

      case "active":
        return (
          <Single id="q-active" title="How active are you now?" options={ACTIVE_OPTS} value={a.active} onPick={(v) => set({ active: v })} />
        );

      case "days": {
        const line = daysLine(a.days, a.minutes);
        return (
          <div className="k-q">
            <div className="k-q__block">
              <QuestionHead id="q-days" title="How many days a week?" />
              <Segmented ariaLabel="Days a week" options={DAYS_OPTS} value={a.days ?? null} onChange={(v) => set({ days: v })} />
            </div>
            <div className="k-q__block">
              <QuestionHead id="q-mins" second title="How long each time?" help="Minutes" />
              <Segmented ariaLabel="Minutes each time" options={MINUTES_OPTS} value={a.minutes ?? null} onChange={(v) => set({ minutes: v })} />
            </div>
            {line ? <p className="k-qfine">{line}</p> : null}
            <p className="k-qfine">Strength days never back to back.</p>
          </div>
        );
      }

      case "when":
        return (
          <div className="k-q__block">
            <QuestionHead id="q-when" title="When suits you?" />
            <QuestionOptions
              labelledBy="q-when"
              single
              options={WHEN_OPTS}
              chosen={a.when ? [a.when] : []}
              onPick={(v) => set({ when: v })}
              follow={(v) =>
                v === "meals" ? (
                  <QuestionFollowUp question="Which meals?" line={mealWalkLine(a.mealWalks) ?? undefined}>
                    <div className="k-chipwrap" role="group" aria-label="Which meals">
                      {MEAL_WALK_OPTS.map((m) => (
                        <Chip
                          key={m.value}
                          size="lg"
                          selected={(a.mealWalks ?? []).includes(m.value)}
                          onClick={() => set({ mealWalks: toggle(a.mealWalks, m.value) })}
                        >
                          {m.label}
                        </Chip>
                      ))}
                    </div>
                  </QuestionFollowUp>
                ) : null
              }
            />
          </div>
        );

      case "have": {
        const have = a.have ?? [];
        const line = haveLine(a.have);
        return (
          <div className="k-q__block">
            <QuestionHead id="q-have" title="What do you have at home?" help="Pick all you have. Water bottles count as weights." />
            <div className="k-chipwrap" role="group" aria-labelledby="q-have">
              {HAVE_OPTS.map((o) => (
                <Chip key={o.value} size="lg" selected={have.includes(o.value)} onClick={() => set({ have: toggle(a.have, o.value) })}>
                  {o.label}
                </Chip>
              ))}
              <Chip size="lg" selected={a.have !== undefined && !have.length} onClick={() => set({ have: [] })}>
                None of these
              </Chip>
            </div>
            {line ? <p className="k-qfine">{line}</p> : null}
          </div>
        );
      }

      case "joints": {
        const joints = a.joints ?? {};
        const keys = Object.keys(joints) as JointKey[];
        const pick = (k: JointKey) => {
          const nextJ = { ...joints };
          if (nextJ[k]) delete nextJ[k];
          else nextJ[k] = {};
          set({ joints: nextJ });
        };
        const setJ = (k: JointKey, patch: Partial<NonNullable<(typeof joints)[JointKey]>>) =>
          set({ joints: { ...joints, [k]: { ...(joints[k] ?? {}), ...patch } } });
        return (
          <div className="k-q__block">
            <QuestionHead id="q-joints" title="Any joint that limits you?" help="Pick any. None is fine." />
            <div className="k-chipwrap" role="group" aria-labelledby="q-joints">
              {JOINT_OPTS.map((o) => (
                <Chip key={o.value} size="lg" selected={keys.includes(o.value)} onClick={() => pick(o.value)}>
                  {o.label}
                </Chip>
              ))}
              <Chip size="lg" selected={a.joints !== undefined && !keys.length} onClick={() => set({ joints: {} })}>
                None
              </Chip>
            </div>
            {keys.map((k) => {
              const v = joints[k] ?? {};
              const word = JOINT_OPTS.find((o) => o.value === k)?.label.toLowerCase() ?? k;
              return (
                <div key={k} className="k-qfree">
                  <QuestionFollowUp count={countWords("jointHow")} question={`Your ${word}: how much?`} line={jointLine(k, v) ?? undefined}>
                    <Segmented ariaLabel={`Your ${word}: how much`} options={JOINT_HOW_OPTS} value={v.how ?? null} onChange={(how) => setJ(k, { how })} />
                    {SIDED.includes(k) ? (
                      <>
                        <p className="k-qfollow__q">Which side?</p>
                        <Segmented ariaLabel={`Which ${word}`} options={SIDE_OPTS} value={v.side ?? null} onChange={(side) => setJ(k, { side })} />
                      </>
                    ) : null}
                  </QuestionFollowUp>
                </div>
              );
            })}
          </div>
        );
      }

      case "falls":
        return (
          <div className="k-q">
            <Single id="q-falls" title="Have you fallen in the last year?" options={FALLS_OPTS} value={a.falls} onPick={(v) => set({ falls: v })} />
            <div className="k-q__block">
              <QuestionHead id="q-unsteady" second title="Do you feel unsteady, or worry about falling?" />
              <QuestionOptions
                labelledBy="q-unsteady"
                single
                options={UNSTEADY_OPTS}
                chosen={a.unsteady ? [a.unsteady] : []}
                onPick={(v) => set({ unsteady: v })}
              />
            </div>
          </div>
        );

      case "body": {
        const chair = chairAsked(Sx, a, today);
        const c = chairAnswer(a);
        return (
          <div className="k-q">
            {chair ? (
              <Single
                id="q-chair"
                title="Can you stand up from a chair without using your hands?"
                options={CHAIR_OPTS}
                value={c}
                onPick={(v) => set(chairPatch(v))}
              />
            ) : null}
            <div className="k-q__block">
              <QuestionHead id="q-floor" second={chair} title="Can you get down to the floor and up again?" />
              <QuestionOptions labelledBy="q-floor" single options={FLOOR_OPTS} chosen={a.floor ? [a.floor] : []} onPick={(v) => set({ floor: v })} />
            </div>
          </div>
        );
      }

      case "breathless":
        return (
          <Single
            id="q-breath"
            title="Do you get breathless walking on the flat at your own pace?"
            options={BREATHLESS_OPTS}
            value={a.breathless}
            onPick={(v) => set({ breathless: v })}
          />
        );

      case "style":
        return (
          <div className="k-q">
            <Single id="q-style" title="How do you like to move?" options={STYLE_OPTS} value={a.style} onPick={(v) => set({ style: v })} />
            <div className="k-rows">
              <NavRow
                kind="toggle"
                title="Voice in the player"
                subtitle="Says the next move, the rest and the count"
                checked={!!a.voice}
                onChange={(v) => set({ voice: v })}
              />
            </div>
            <div className="k-q__block">
              <QuestionHead id="q-lang" second title="The player’s language" />
              <Segmented
                ariaLabel="The player’s language"
                options={[
                  { value: "en", label: "English" },
                  { value: "hi", label: <span lang="hi">हिंदी</span> },
                ]}
                value={a.lang ?? "en"}
                onChange={(v) => set({ lang: v })}
              />
            </div>
          </div>
        );

      case "tests":
        return renderTests();
    }
  };

  const isStart = cur === "start";
  const progress = (id: ScreenId) => {
    const c = trainingCount(Sx, a, id, today, asking);
    return c ? <QuestionProgress n={c.n} of={c.of} /> : null;
  };

  const actions = (): ReactNode => {
    if (isStart) return <ActionBar primary={{ label: "Start", onClick: () => go(screens[0]) }} />;
    if (cur === "readback")
      return (
        <ActionBar
          secondary={{
            label: "Change an answer",
            onClick: () => go(params.screen && screens.includes(params.screen as ScreenId) ? (params.screen as ScreenId) : lastScreen),
          }}
          primary={{ label: subject ? "Save" : "See the plan", onClick: save }}
        />
      );
    if (cur === "tests" && !subject)
      return (
        <ActionBar
          secondary={{ label: "Back", onClick: back }}
          also={{ label: "Later", onClick: () => go("readback") }}
          primary={{
            label: "Do them now",
            // the draft is kept and the read-back waits: P4's tests come back to it
            // and keeps where the questions came from, so the read-back still returns to You › Training
            onClick: () =>
              router.push(
                `/train/tests?then=${encodeURIComponent(`/train/questions/readback${from === "/you/training" ? "?from=you" : ""}`)}`,
                "forward"
              ),
          }}
        />
      );
    return (
      <ActionBar
        secondary={{ label: "Back", onClick: back }}
        primary={{
          label: cur === "still" ? "Yes, still right" : "Next",
          onClick: next,
          disabled: !answered(cur),
          reason: reasonFor(cur),
        }}
      />
    );
  };

  return (
    <IonContent ref={content}>
      <Screen>
        <div className="k-large k-q">
          {head}
          {!isStart && cur !== "readback" ? progress(cur) : null}
          {body()}
        </div>
        <div className="k-large">{actions()}</div>
      </Screen>
      {change ? <ChangeSheet which={change} S={S} onClose={() => setChange(null)} /> : null}
    </IonContent>
  );

  /* ---------- the start (board TrainingStart) ---------- */

  function renderStart() {
    const n = trainingSteps(Sx, a, today, asking).length;
    const tests = screens.includes("tests");
    const years = age(S, today);
    const ws = weightSeries(S);
    const last = ws.length ? ws[ws.length - 1] : null;
    const sex = S.profile.sex === "female" ? "Female" : S.profile.sex === "male" ? "Male" : "–";
    return (
      <>
        {subject ? (
          <Notice tone="info" icon={I.user}>
            You are answering for {subject.name}. Read {subject.pron.them} each question; what you save reaches {subject.pron.their} phone
            the next time it syncs. The two safety questions are left for {subject.pron.their} own phone.
          </Notice>
        ) : null}
        <Hero
          label="About"
          figure={4}
          unit="minutes, all taps"
          line={`${n} questions, one a screen.${tests && isOlder ? " The two tests need a chair and someone nearby." : ""}`}
          visual={
            <ul className="k-qstart__lines">
              <li>
                {I.tick}Kaya plans your week around what you can do today.
              </li>
              <li>
                {I.tick}Your health answers from Plan my food are read back, not asked again.
              </li>
              <li>
                {I.tick}Change any answer later, under You › Training.
              </li>
            </ul>
          }
        />
        <div>
          <RowGroup heading="Kaya already knows">
            <NavRow kind="value" icon={I.user} title="Sex" value={sex} />
            <NavRow
              kind="value"
              icon={I.calendar}
              title="Year of birth"
              value={S.profile.birthYear ? `${S.profile.birthYear}${years !== null ? ` · ${years}` : ""}` : "–"}
            />
            <NavRow kind="value" icon={I.tape} title="Height" value={S.profile.heightCm ? `${S.profile.heightCm} cm` : "–"} />
            <NavRow
              kind="value"
              icon={I.scale}
              title="Weight"
              subtitle={last ? dayLabel(last.date) : undefined}
              value={last ? `${last.v.toFixed(1)} kg` : `${curWeight(S).toFixed(1)} kg`}
            />
          </RowGroup>
          <p className="k-qfine k-qfine--under">
            {subject ? (
              <>Not asked again. {subject.name} changes them on {subject.pron.their} phone, under You › Body & goals.</>
            ) : (
              <>
                Not asked again. Change them under <Link to="/you/body">Body & goals</Link>.
              </>
            )}
          </p>
        </div>
        <Notice tone="caution">
          This is exercise guidance, not medical advice. If a doctor or physiotherapist has given you exercises, do theirs.
        </Notice>
      </>
    );
  }

  /* ---------- the conditions, when Plan my food has not asked them (no board; see ADR-053) ---------- */

  function renderHealth() {
    const h = health ?? {};
    const conds = h.conditions ?? [];
    const opts: QuestionOption<string>[] = [
      ...CONDITION_OPTS,
      { value: NONE, label: "None" },
      { value: "private", label: "Rather not say" },
    ];
    const chosen = h.conditionsPrivate ? ["private"] : h.conditions === undefined ? [] : conds.length ? conds : [NONE];
    const pick = (v: string) => {
      if (v === "private") return setH({ conditions: [], conditionsPrivate: true, hypoAnswer: undefined, hypoRisk: undefined });
      if (v === NONE) return setH({ conditions: [], conditionsPrivate: undefined, hypoAnswer: undefined, hypoRisk: undefined });
      const nextC = toggle(h.conditions, v as EatingCondition);
      setH({
        conditions: nextC,
        conditionsPrivate: undefined,
        ...(nextC.includes("diabetes") ? {} : { hypoAnswer: undefined, hypoRisk: undefined }),
      });
    };
    return (
      <div className="k-q">
        {pregnancyAsked(Sx, today) ? (
          <Single
            id="q-preg"
            title="Are you pregnant or breastfeeding?"
            options={PREGNANT_OPTS.map((o) => ({ ...o, sub: o.value === "no" ? undefined : "Walking and the gentle sessions; your midwife or doctor sets the rest" }))}
            value={h.pregnant}
            onPick={(v) => setH({ pregnant: v })}
          />
        ) : null}
        <div className="k-q__block">
          <QuestionHead id="q-cond" second={pregnancyAsked(Sx, today)} title="Any of these?" help="Pick all that apply. Plan my food uses the same answer." />
          <QuestionOptions
            labelledBy="q-cond"
            options={opts}
            chosen={chosen}
            onPick={pick}
            follow={(v) =>
              v === "bp" ? (
                bpFollow
              ) : v === "diabetes" ? (
                <>
                  <QuestionFollowUp question="Do you take insulin, or tablets your doctor said can cause low sugar?">
                    <Segmented
                      ariaLabel="Insulin or low-sugar tablets"
                      options={HYPO_OPTS}
                      value={h.hypoAnswer ?? null}
                      onChange={(x) => setH({ hypoAnswer: x, hypoRisk: x !== "no" })}
                    />
                  </QuestionFollowUp>
                  {sugarFollow}
                </>
              ) : null
            }
          />
        </div>
        {medsAsked(Sx, today) ? (
          <div className="k-q__block">
            <QuestionHead id="q-meds" second title="Do you take any of these?" help="Pick all that apply." />
            <QuestionOptions
              labelledBy="q-meds"
              options={[...MED_OPTS.map(({ value, label }) => ({ value, label })), { value: NONE, label: "None" }]}
              chosen={h.meds === undefined ? [] : h.meds.length ? h.meds : [NONE]}
              onPick={(v) => setH({ meds: v === NONE ? [] : toggle(h.meds, v as (typeof MED_OPTS)[number]["value"]) })}
            />
            <p className="k-qfine">BP tablets: effort by the talk test, and stand up slowly.</p>
          </div>
        ) : null}
      </div>
    );
  }

  /* ---------- the tests step (board TrainingTests; the tests themselves are P4's) ---------- */

  function renderTests() {
    const offered = testsOffered(Sx, a, today);
    if (subject) {
      return (
        <div className="k-q__block">
          <QuestionHead
            id="q-tests"
            title={shortTestsTitle(offered.length)}
            help={`Ask ${subject.pron.them} to do these on ${subject.pron.their} phone; or type the counts you timed.`}
          />
          <Field
            label="Chair stands in 30 seconds"
            type="number"
            value={typed.chair}
            onChange={(v) => setTyped((t) => ({ ...t, chair: v }))}
            min="0"
            max="40"
            placeholder="Not timed"
          />
          {offered.includes("balance") ? (
            <Field
              label="Heel-to-toe stand, seconds held"
              type="number"
              value={typed.tandem}
              onChange={(v) => setTyped((t) => ({ ...t, tandem: v }))}
              min="0"
              max="10"
              unit="s"
              hint="Up to 10. A counter within reach, and you beside them."
              placeholder="Not timed"
            />
          ) : null}
          <p className="k-qfine">Kaya shows the figure beside the usual range for {subject.pron.their} age. It is not a grade.</p>
        </div>
      );
    }
    const by = S.profile.training?.setBy;
    return (
      <div className="k-q__block">
        <QuestionHead id="q-tests" title={shortTestsTitle(offered.length)} />
        <RowGroup heading={`${offered.length === 2 ? "2" : "5"} minutes · ${isOlder ? "a chair, and someone beside you" : "a chair, a wall and a timer"}`}>
          {offered.map((k) => (
            <NavRow key={k} kind="value" icon={TEST_ICON[k]} title={TEST_WORDS[k].title} subtitle={TEST_WORDS[k].sub} />
          ))}
        </RowGroup>
        {by ? <p className="k-qfine">This is the one step {by} can’t answer for you: it needs you, a chair and someone beside you.</p> : null}
        <p className="k-qfine">
          {isOlder
            ? "Kaya shows your figure beside the usual range for your age. It is not a grade."
            : "They place you in the programme instead of starting at week 1. Your figures, never a grade."}
        </p>
      </div>
    );
  }

  /* ---------- the read-back (board TrainingReadBack) ---------- */

  function renderReadBack() {
    // answered in their name, the read-back is about them, not "you" (ADR-056, P9)
    const about = aboutWhom(subject ? subject.pron : null);
    const sentences = readBack(Sx, a, today).map(about);
    const placed = tested ? placement(Sx, tested.tests, today) : null;
    const line = about(planLine(Sx, a, tested?.tests ?? null, device, today));
    const changes = whatChanges(Sx, a, tested, device, today);
    return (
      <div className="k-q__block">
        <QuestionHead id="q-back" title={subject ? `${subject.name}’s answers` : "Your answers"} help="Check them before Kaya uses them." />
        {!subject && S.profile.training?.setBy ? (
          // what a linked owner set is named on the person's own phone (ADR-056)
          <p className="k-qfine">
            <Tag tone="neutral">set by {S.profile.training.setBy}</Tag> Saving them here makes them yours.
          </p>
        ) : null}
        <ul className="k-qback">
          {sentences.map((s) => (
            <li key={s}>{s}</li>
          ))}
        </ul>
        {placed && tested ? (
          <RowGroup
            heading={`Placed by ${subject ? `${subject.pron.their} ` : "your "}tests · ${dayLabel(tested.ds)}${
              tested.tests.by ? ` · typed by ${tested.tests.by}` : ""
            }`}
          >
            <NavRow kind="value" icon={I.balance} title={placed.level === "steady" ? "Steady" : "Build"} subtitle={placed.line} />
          </RowGroup>
        ) : null}
        <p className="k-qplan">{line}</p>
        <RowGroup heading="What changes">
          <NavRow kind="value" icon={I.calendar} title={subject ? `${subject.pron.Their} week` : "Your week"} subtitle={about(changes.week)} />
          <NavRow kind="value" icon={I.sun} title="Today" subtitle={about(changes.today)} />
          <NavRow kind="value" icon={I.clock} title="Retest" subtitle={about(changes.retest)} />
        </RowGroup>
      </div>
    );
  }
}

const TEST_WORDS: Record<TestKey, { title: string; sub: string }> = {
  chair: { title: "Chair stand", sub: "Stand up and sit down as often as you can in 30 seconds. Kaya counts and times." },
  balance: { title: "Balance", sub: "Four ways of standing, 10 seconds each, a counter in reach." },
  pushups: { title: "Wall push-ups", sub: "As many as you can with good form." },
  plank: { title: "Plank", sub: "Held as long as your back stays straight." },
  step: { title: "2-minute step", sub: "March on the spot; Kaya counts the right knee." },
};

const TEST_ICON: Record<TestKey, ReactNode> = {
  chair: I.legs,
  balance: I.balance,
  pushups: I.upper,
  plank: I.core,
  step: I.footsteps,
};

/** Pregnancy is food's question, asked of women of 18–50 and of a woman whose age is not known. */
function pregnancyAsked(S: KayaState, today: Date): boolean {
  const y = age(S, today);
  return S.profile.sex === "female" && (y === null || (y >= PREGNANCY_AGES.from && y <= PREGNANCY_AGES.to));
}

/** Medicines are food's question too, asked at 60+ or with a condition. */
function medsAsked(S: KayaState, today: Date): boolean {
  return older(S, today) || (S.profile.eating?.conditions ?? []).length > 0;
}

function withoutStamp(t: DayTests): Omit<DayTests, "at"> {
  const { at: _at, ...rest } = t;
  return rest;
}

/** The counts typed on someone else's phone, as a test block for today. */
function typedTests(t: { chair: string; tandem: string }, today: Date): { ds: string; tests: DayTests } | null {
  const chair = t.chair.trim() === "" ? undefined : Math.max(0, Math.round(Number(t.chair)));
  const secs = t.tandem.trim() === "" ? undefined : Math.max(0, Math.min(10, Number(t.tandem)));
  if ((chair === undefined || Number.isNaN(chair)) && (secs === undefined || Number.isNaN(secs))) return null;
  const tests: DayTests = { at: today.getTime() };
  if (chair !== undefined && !Number.isNaN(chair)) tests.chairStand = chair;
  if (secs !== undefined && !Number.isNaN(secs)) {
    // a typed tandem time assumes the two easier stands were held, as STEADI's order has them
    if (secs >= 10) tests.balanceStage = 3;
    else {
      tests.balanceStage = 2;
      tests.balanceSecs = secs;
    }
  }
  return { ds: todayStr(today), tests };
}

/* ---------- one-answer screens ---------- */

function Single<T extends string>({
  id,
  title,
  help,
  options,
  value,
  onPick,
}: {
  id: string;
  title: string;
  help?: string;
  options: readonly QuestionOption<T>[];
  value: T | undefined;
  onPick: (v: T) => void;
}) {
  return (
    <div className="k-q__block">
      <QuestionHead id={id} title={title} help={help} />
      <QuestionOptions labelledBy={id} single options={options} chosen={value ? [value] : []} onPick={onPick} />
    </div>
  );
}

/** "Your last reading, if you know it": optional; none with BP softens the plan. */
function BpReading({
  value,
  onChange,
}: {
  value: TrainingProfile["bpReading"];
  onChange: (v: TrainingProfile["bpReading"]) => void;
}) {
  const [sys, setSys] = useState(value ? String(value.sys) : "");
  const [dia, setDia] = useState(value ? String(value.dia) : "");
  const commit = (s: string, d: string) => {
    const a = Number(s);
    const b = Number(d);
    onChange(s && d && a >= 70 && a <= 260 && b >= 40 && b <= 160 ? { sys: Math.round(a), dia: Math.round(b) } : undefined);
  };
  return (
    <div className="k-qbp">
      <p className="k-qfollow__q">Your last reading, if you know it</p>
      <div className="k-qbp__pair">
        <Field
          label="Upper"
          type="number"
          value={sys}
          onChange={(v) => {
            setSys(v);
            commit(v, dia);
          }}
          min="70"
          max="260"
          placeholder="e.g. 140"
        />
        <Field
          label="Lower"
          type="number"
          value={dia}
          onChange={(v) => {
            setDia(v);
            commit(sys, v);
          }}
          min="40"
          max="160"
          placeholder="e.g. 85"
        />
      </div>
      <p className="k-qfollow__line">160/90 or more, or not known: no fast work. 180/110 or more: no session that day.</p>
    </div>
  );
}

/* ---------- Change, from Still right?: food's own question in a sheet ---------- */

function ChangeSheet({ which, S, onClose }: { which: StillKey; S: KayaState; onClose: () => void }) {
  const update = useKaya((s) => s.update);
  const [e, setE] = useState<EatingAnswers>(eatingOf(S.profile.eating));
  const today = new Date();
  const setP = (p: Partial<EatingAnswers>) => setE((c) => ({ ...c, ...p }));

  const save = () => {
    update((d) => {
      setEatingProfile(d, pruneAnswers(d, e, today));
    });
    onClose();
  };

  let title = "";
  let bodyNode: ReactNode = null;
  let ok = true;
  switch (which) {
    case "pregnant":
      title = "Pregnant or breastfeeding?";
      bodyNode = (
        <QuestionOptions labelledBy="" single options={PREGNANT_OPTS} chosen={e.pregnant ? [e.pregnant] : []} onPick={(v) => setP({ pregnant: v })} />
      );
      break;
    case "conditions": {
      const conds = e.conditions ?? [];
      const opts: QuestionOption<string>[] = [...CONDITION_OPTS, { value: NONE, label: "None" }, { value: "private", label: "Rather not say" }];
      const chosen = e.conditionsPrivate ? ["private"] : conds.length ? conds : [NONE];
      title = "Any of these?";
      ok = !conds.includes("diabetes") || !!e.hypoAnswer;
      bodyNode = (
        <QuestionOptions
          labelledBy=""
          options={opts}
          chosen={chosen}
          onPick={(v) => {
            if (v === "private") return setP({ conditions: [], conditionsPrivate: true, hypoAnswer: undefined, hypoRisk: undefined });
            if (v === NONE) return setP({ conditions: [], conditionsPrivate: undefined, hypoAnswer: undefined, hypoRisk: undefined });
            const nextC = toggle(e.conditions, v as EatingCondition);
            setP({ conditions: nextC, conditionsPrivate: undefined, ...(nextC.includes("diabetes") ? {} : { hypoAnswer: undefined, hypoRisk: undefined }) });
          }}
          follow={(v) =>
            v === "diabetes" ? (
              <QuestionFollowUp question="Do you take insulin, or tablets your doctor said can cause low sugar?">
                <Segmented
                  ariaLabel="Insulin or low-sugar tablets"
                  options={HYPO_OPTS}
                  value={e.hypoAnswer ?? null}
                  onChange={(x) => setP({ hypoAnswer: x, hypoRisk: x !== "no" })}
                />
              </QuestionFollowUp>
            ) : null
          }
        />
      );
      break;
    }
    case "meds":
      title = "Do you take any of these?";
      bodyNode = (
        <QuestionOptions
          labelledBy=""
          options={[...MED_OPTS.map(({ value, label }) => ({ value, label })), { value: NONE, label: "None" }]}
          chosen={e.meds === undefined ? [] : e.meds.length ? e.meds : [NONE]}
          onPick={(v) => setP({ meds: v === NONE ? [] : toggle(e.meds, v as (typeof MED_OPTS)[number]["value"]) })}
        />
      );
      break;
    case "sleep":
      title = "How much do you usually sleep?";
      bodyNode = (
        <>
          <QuestionOptions labelledBy="" single options={SLEEP_OPTS} chosen={e.sleep ? [e.sleep] : []} onPick={(v) => setP({ sleep: v })} />
          <QuestionOptions
            labelledBy=""
            options={[{ value: "yes", label: "I work night or rotating shifts" }]}
            chosen={e.shift ? ["yes"] : []}
            onPick={() => setP({ shift: !e.shift })}
          />
        </>
      );
      break;
    case "periods":
      break;
  }

  return (
    <Sheet
      large
      title={title}
      sub="Plan my food reads the same answer."
      onClose={onClose}
      footer={
        <ActionBar
          inSheet
          secondary={{ label: "Cancel", onClick: onClose }}
          primary={{ label: "Save", onClick: save, disabled: !ok, reason: "Answer the follow-up too" }}
        />
      }
    >
      <div className="k-q">{bodyNode}</div>
    </Sheet>
  );
}
